import { API_BASE } from '../../config/apiBase';
import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { CheckCircle, Clock, Phone, FileText, Shield, Upload, Truck } from 'lucide-react';
import { cn } from '../../lib/utils';

const API = API_BASE;

const STATUS_ICONS = {
  deal_created: Clock,
  loi_generated: FileText,
  loi_sent: FileText,
  loi_viewed: FileText,
  activation_pending: Clock,
  activated: CheckCircle,
  conditional_approval_issued: Shield,
  documents_pending: Upload,
  final_review: FileText,
  approved_for_funding: CheckCircle,
  funded: Truck,
};

const CustomerPortal = () => {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchPortal = async () => {
      try {
        const res = await axios.get(`${API}/deals/public/portal/${token}`);
        setData(res.data);
      } catch (err) {
        setError(err.response?.data?.detail || 'Invalid link');
      } finally {
        setLoading(false);
      }
    };
    fetchPortal();
  }, [token]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-950 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-500" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-950 flex items-center justify-center p-4">
        <h2 className="text-2xl font-bold text-white">Link Not Found</h2>
        <p className="text-gray-400 mt-2">{error}</p>
      </div>
    );
  }

  const pipeline = data?.pipeline || [];
  const checklist = data?.checklist || [];

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-950 via-gray-900 to-gray-950 py-8 px-4">
      <div className="max-w-3xl mx-auto space-y-6">
        {/* Header */}
        <div className="text-center space-y-2">
          <p className="text-gray-400 text-sm">Welcome back</p>
          <h1 className="text-2xl sm:text-3xl font-bold text-white">{data?.customer_name}</h1>
          {data?.company_name && <p className="text-gray-400">{data.company_name}</p>}
        </div>

        {/* Current Status Badge */}
        <div className="flex justify-center">
          <Badge className={cn(
            "text-sm px-4 py-2",
            data?.status === 'funded' ? "bg-emerald-500/20 text-emerald-400 border-emerald-500/30" :
            data?.status === 'activated' ? "bg-blue-500/20 text-blue-400 border-blue-500/30" :
            "bg-amber-500/20 text-amber-400 border-amber-500/30"
          )} data-testid="current-status-badge">
            {data?.status_label?.en || data?.status}
          </Badge>
        </div>

        {/* Status Pipeline */}
        <Card className="bg-white/5 border-white/10" data-testid="status-pipeline">
          <CardContent className="p-4 overflow-x-auto">
            <div className="flex items-center justify-between min-w-[700px]">
              {pipeline.map((stage, i) => {
                const Icon = STATUS_ICONS[stage.status] || Clock;
                return (
                  <div key={stage.status} className="flex items-center">
                    <div className="flex flex-col items-center">
                      <div className={cn(
                        "w-8 h-8 rounded-full flex items-center justify-center transition-all",
                        stage.completed ? "bg-emerald-500 text-white" :
                        stage.active ? "bg-red-500 text-white ring-2 ring-red-400 ring-offset-2 ring-offset-gray-900" :
                        "bg-gray-700 text-gray-500"
                      )}>
                        {stage.completed ? <CheckCircle className="w-4 h-4" /> : <Icon className="w-4 h-4" />}
                      </div>
                      <span className={cn(
                        "text-[10px] mt-1 max-w-[70px] text-center leading-tight",
                        stage.active ? "text-white font-medium" : stage.completed ? "text-emerald-400" : "text-gray-600"
                      )}>
                        {stage.label?.en || stage.status}
                      </span>
                    </div>
                    {i < pipeline.length - 1 && (
                      <div className={cn(
                        "h-px w-6 mx-1",
                        stage.completed ? "bg-emerald-500" : "bg-gray-700"
                      )} />
                    )}
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {/* Progress */}
        <Card className="bg-white/5 border-white/10" data-testid="progress-card">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-3">
              <span className="text-white font-medium">Progress</span>
              <span className="text-2xl font-bold text-white">{data?.progress_pct}%</span>
            </div>
            <div className="h-3 bg-gray-800 rounded-full overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-red-600 to-red-400 rounded-full transition-all duration-500"
                style={{ width: `${data?.progress_pct || 0}%` }}
              />
            </div>
            <p className="text-gray-500 text-sm mt-2">
              You're {data?.progress_pct}% complete — {data?.completed_items}/{data?.total_items} items done
            </p>
          </CardContent>
        </Card>

        {/* Checklist */}
        <Card className="bg-white/5 border-white/10" data-testid="checklist-card">
          <CardContent className="p-6">
            <h3 className="text-white font-semibold mb-4">Required Steps</h3>
            <div className="space-y-3">
              {checklist.map((item) => (
                <div
                  key={item.id}
                  className={cn(
                    "flex items-center gap-3 p-3 rounded-lg border transition-all",
                    item.completed
                      ? "bg-emerald-500/5 border-emerald-500/20"
                      : "bg-white/5 border-white/10"
                  )}
                  data-testid={`checklist-${item.id}`}
                >
                  <div className={cn(
                    "w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0",
                    item.completed ? "bg-emerald-500" : "bg-gray-700"
                  )}>
                    {item.completed ? (
                      <CheckCircle className="w-4 h-4 text-white" />
                    ) : (
                      <Clock className="w-3 h-3 text-gray-400" />
                    )}
                  </div>
                  <span className={cn(
                    "text-sm",
                    item.completed ? "text-emerald-400 line-through" : "text-white"
                  )}>
                    {item.label_en}
                  </span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Equipment Details */}
        <Card className="bg-white/5 border-white/10">
          <CardContent className="p-6 space-y-3">
            <h3 className="text-white font-semibold">Equipment Details</h3>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <p className="text-gray-500">Equipment</p>
                <p className="text-white font-medium">{data?.equipment_description}</p>
              </div>
              <div>
                <p className="text-gray-500">Cost</p>
                <p className="text-white font-medium">${(data?.equipment_cost || 0).toLocaleString()}</p>
              </div>
              <div>
                <p className="text-gray-500">Term</p>
                <p className="text-white font-medium">{data?.terms?.term_months} months</p>
              </div>
              <div>
                <p className="text-gray-500">Program</p>
                <p className="text-white font-medium">{data?.terms?.program_type || 'EFA'}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Funding Manager */}
        {data?.funding_manager && (
          <Card className="bg-white/5 border-white/10">
            <CardContent className="p-6 flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-red-600 flex items-center justify-center flex-shrink-0">
                <Phone className="w-5 h-5 text-white" />
              </div>
              <div>
                <p className="text-gray-400 text-sm">Your Funding Manager</p>
                <p className="text-white font-semibold">{data.funding_manager}</p>
                {data.funding_manager_phone && (
                  <a href={`tel:${data.funding_manager_phone}`} className="text-red-400 hover:text-red-300 text-sm">
                    {data.funding_manager_phone}
                  </a>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        <p className="text-center text-gray-600 text-xs py-4">
          Inspired Funding LLC - Trusted Commercial Financing Provider
        </p>
      </div>
    </div>
  );
};

export default CustomerPortal;
